import gifsicle from 'gifsicle-wasm-browser';

const CLOUD_NAME = 'univerlist';
const UPLOAD_PRESET = 'admin-upload';
// Isolate auto-convert source MP4s under a dedicated path so they can be
// spotted or cleaned up without touching the rest of the admin assets.
// We set this via `public_id` (not `folder`) because the upload preset's
// "Asset folder" setting otherwise overrides an unsigned `folder` param.
const UPLOAD_FOLDER = 'prompt-auto-convert';
// Gifsicle lossy level applied on top of Cloudinary's full-palette GIF.
// Higher = smaller file but LZW-aware dithering adds visible grain on
// smooth gradients (e.g. chocolate/skin). 10 keeps the output clean while
// still trimming ~35% off the raw GIF.
const GIFSICLE_LOSSY = 10;

function randomPublicIdSuffix() {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand}`;
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function uploadToCloudinary(file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/video/upload`);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress?.(Math.min(Math.round((e.loaded / e.total) * 100), 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (_) {
          reject(new Error('Cloudinary yanıtı okunamadı'));
        }
      } else {
        let msg = `Yukleme basarisiz (${xhr.status})`;
        try {
          const j = JSON.parse(xhr.responseText);
          if (j?.error?.message) msg = j.error.message;
        } catch (_) {}
        reject(new Error(msg));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Upload sirasinda ag hatasi')));
    xhr.addEventListener('abort', () => reject(new Error('Upload iptal edildi')));
    const form = new FormData();
    form.append('file', file);
    form.append('upload_preset', UPLOAD_PRESET);
    form.append('public_id', `${UPLOAD_FOLDER}/${randomPublicIdSuffix()}`);
    xhr.send(form);
  });
}

function downloadWithProgress(url, filename, mime, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'arraybuffer';
    xhr.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        onProgress?.(Math.min(Math.round((e.loaded / e.total) * 100), 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`${filename} indirilemedi (${xhr.status})`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error(`${filename} indirilirken ag hatasi`)));
    xhr.send();
  });
}

// Run the Cloudinary GIF through gifsicle-wasm for ezgif-style lossy LZW
// compression + -O3 structural optimization. Input/output are Blobs.
async function optimizeGifWithGifsicle(inputBuffer) {
  const inputFile = new File([inputBuffer], 'in.gif', { type: 'image/gif' });
  const outFiles = await gifsicle.run({
    input: [{ file: inputFile, name: 'in.gif' }],
    command: [
      `-O3 --lossy=${GIFSICLE_LOSSY} --loopcount=forever in.gif -o /out/out.gif`,
    ],
  });
  if (!outFiles || outFiles.length === 0) {
    throw new Error('Gifsicle optimizasyonu basarisiz');
  }
  return await outFiles[0].arrayBuffer();
}

// Cloudinary's GIF delivery omits the NETSCAPE2.0 application extension that
// signals "loop forever" to viewers, so the resulting GIF plays once and stops.
// Detect its absence and splice one in right after the Logical Screen Descriptor
// (and Global Color Table, if present) without touching frame data.
function ensureGifLoops(arrayBuffer) {
  const src = new Uint8Array(arrayBuffer);
  if (src.length < 14) return arrayBuffer;
  // Sanity: GIF87a / GIF89a header.
  if (String.fromCharCode(src[0], src[1], src[2]) !== 'GIF') return arrayBuffer;

  const marker = [0x21, 0xff, 0x0b, 0x4e, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45, 0x32, 0x2e, 0x30];
  outer: for (let i = 0; i <= src.length - marker.length; i++) {
    for (let j = 0; j < marker.length; j++) {
      if (src[i + j] !== marker[j]) continue outer;
    }
    return arrayBuffer;
  }

  // Header (6) + Logical Screen Descriptor (7); packed byte at offset 10.
  const packed = src[10];
  let insertAt = 13;
  if (packed & 0x80) {
    const gctSize = 3 * (1 << ((packed & 0x07) + 1));
    insertAt = 13 + gctSize;
  }
  if (insertAt > src.length) return arrayBuffer;

  const loopBlock = new Uint8Array([
    0x21, 0xff, 0x0b,
    0x4e, 0x45, 0x54, 0x53, 0x43, 0x41, 0x50, 0x45, 0x32, 0x2e, 0x30,
    0x03, 0x01, 0x00, 0x00,
    0x00,
  ]);
  const out = new Uint8Array(src.length + loopBlock.length);
  out.set(src.subarray(0, insertAt), 0);
  out.set(loopBlock, insertAt);
  out.set(src.subarray(insertAt), insertAt + loopBlock.length);
  return out.buffer;
}

// Cloudinary-delivered animated WebPs carry ANIM.loop_count = 1, so viewers
// play them once and freeze. Rewrite it to 0 (infinite) in the 2-byte LE field
// that lives at the end of the ANIM chunk payload.
function ensureWebpLoops(arrayBuffer) {
  const src = new Uint8Array(arrayBuffer);
  if (src.length < 30) return arrayBuffer;
  if (
    String.fromCharCode(src[0], src[1], src[2], src[3]) !== 'RIFF' ||
    String.fromCharCode(src[8], src[9], src[10], src[11]) !== 'WEBP'
  ) {
    return arrayBuffer;
  }
  for (let i = 12; i <= src.length - 8; i++) {
    if (
      src[i] === 0x41 && // 'A'
      src[i + 1] === 0x4e && // 'N'
      src[i + 2] === 0x49 && // 'I'
      src[i + 3] === 0x4d // 'M'
    ) {
      // ANIM payload layout: 4B background color, 2B loop count (LE).
      const loopOffset = i + 8 + 4;
      if (loopOffset + 2 > src.length) return arrayBuffer;
      const out = new Uint8Array(src);
      out[loopOffset] = 0x00;
      out[loopOffset + 1] = 0x00;
      return out.buffer;
    }
  }
  return arrayBuffer;
}

function buildDeliveryUrl(publicId, transformation, ext) {
  // Encode each path segment but keep slashes (Cloudinary treats them as folders).
  const safeId = publicId.split('/').map(encodeURIComponent).join('/');
  const path = transformation ? `${transformation}/${safeId}.${ext}` : `${safeId}.${ext}`;
  return `https://res.cloudinary.com/${CLOUD_NAME}/video/upload/${path}`;
}

/**
 * Converts an MP4 file to WebM, WebP (animated), and GIF using Cloudinary.
 *
 * Flow:
 *   1. Uploads the source MP4 once via unsigned upload preset.
 *   2. Builds three delivery URLs (Cloudinary transcodes on-the-fly and caches).
 *   3. Downloads each variant as a File ready for the existing S3 upload flow.
 *
 * Output specs:
 *   WebM – original resolution/fps + Opus audio preserved (Cloudinary default VP9)
 *   WebP – animated, height capped at 360 (c_limit preserves aspect ratio),
 *          loop_count rewritten to infinite
 *   GIF  – animated, height capped at 360, source fps (capped at 30),
 *          post-processed with gifsicle for size + forever loop
 */
export async function convertVideoToFormats(videoFile, onStatus) {
  onStatus?.({ step: 'loading', message: 'Video yukleniyor...', progress: 0 });

  const uploadResult = await uploadToCloudinary(videoFile, (pct) => {
    onStatus?.({
      step: 'loading',
      message: `Video yukleniyor... %${pct}`,
      progress: pct,
    });
  });

  const publicId = uploadResult.public_id;
  if (!publicId) {
    throw new Error('Cloudinary public_id donmedi');
  }

  // Target fps = source fps capped at 30 (spec: "match source fps, max 30").
  // Cloudinary's default GIF delivery drops to ~10 fps which looks choppy, so
  // we always force an explicit fps on the animated output.
  const sourceFps = Number(uploadResult.frame_rate) || 24;
  const gifFps = Math.max(1, Math.min(30, Math.round(sourceFps)));

  onStatus?.({
    step: 'loading',
    message: 'Yukleme tamam, donusumler hazirlaniyor...',
    progress: 100,
    done: true,
  });

  const variants = [
    { key: 'webm', transform: '',                                                     ext: 'webm', mime: 'video/webm', filename: 'video.webm' },
    // Animated WebP requires BOTH fl_awebp (animated output format) and fl_animated;
    // without fl_awebp the delivery falls back to a single-frame (VP8) still.
    { key: 'webp', transform: 'fl_awebp,fl_animated,h_360,c_limit',                   ext: 'webp', mime: 'image/webp', filename: 'image.webp' },
    // No fl_lossy here – Cloudinary's lossy GIF encoder posterizes smooth
    // gradients. We take the full per-frame palette output (~8-9 MB) and
    // shrink it locally with gifsicle, which keeps quality much closer to
    // the ezgif reference.
    { key: 'gif',  transform: `fps_${gifFps},h_360,c_limit`,                          ext: 'gif',  mime: 'image/gif',  filename: 'animation.gif' },
  ];

  const results = {};

  for (const v of variants) {
    const url = buildDeliveryUrl(publicId, v.transform, v.ext);
    onStatus?.({
      step: v.key,
      message: `${v.key.toUpperCase()} hazirlaniyor...`,
      progress: 0,
    });
    let buffer = await downloadWithProgress(url, v.filename, v.mime, (pct) => {
      onStatus?.({
        step: v.key,
        message: `${v.key.toUpperCase()} indiriliyor... %${pct}`,
        progress: pct,
      });
    });
    if (v.key === 'gif') {
      onStatus?.({
        step: v.key,
        message: 'GIF optimize ediliyor (gifsicle)...',
        progress: 100,
      });
      buffer = await optimizeGifWithGifsicle(buffer);
      buffer = ensureGifLoops(buffer);
    } else if (v.key === 'webp') {
      buffer = ensureWebpLoops(buffer);
    }
    const file = new File([buffer], v.filename, { type: v.mime });
    results[v.key] = file;
    onStatus?.({
      step: v.key,
      message: `${v.key.toUpperCase()} hazir (${formatFileSize(file.size)})`,
      progress: 100,
      done: true,
    });
  }

  return results;
}
